import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { newHttpService } from '../../services/new-http-service'
import { utilService } from '../../services/util-service'
import { SlideDrawer } from '../general/slide-drawer'
import { Spinner } from '../util-cmps/spinner'

interface AuditTrailEntry {
    id: number
    company_name: string
    actor_email: string
    actor_name: string | null
    action: string
    resource_type: string
    resource_id: string
    old_value: Record<string, any> | any[] | null
    new_value: Record<string, any> | any[] | null
    reason: string | null
    status: string
    ip_address: string | null
    request_id: string | null
    created_at: string
}

interface AuditTrailListResponse {
    items: AuditTrailEntry[]
    total: number
    limit: number
    offset: number
}

interface AuditTrailFeedProps {
    companyName?: string
    hideCompanyFilter?: boolean
    auditScope?: 'global' | 'tenant'
}

type AuditSort = 'date_desc' | 'date_asc'

const DEFAULT_LIMIT = 25
const GLOBAL_AUDIT_ACTIONS = new Set([
    'DELETE_GLUE_TRIGGER',
    'TOGGLE_GLUE_TRIGGER_STATE',
])
const AUDIT_PARAM_KEYS = [
    'auditPage',
    'auditSort',
    'auditScope',
    'auditSearch',
    'auditCompany',
    'auditResourceType',
    'auditResourceId',
    'auditAction',
    'auditActorEmail',
    'auditStatus',
] as const

function formatSelectLabel(value: string): string {
    if (!value) return 'All'
    return value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

function uniqueSorted(values: Array<string | null | undefined>): string[] {
    return Array.from(
        new Set(values.map(value => value?.trim()).filter((value): value is string => !!value))
    ).sort((a, b) => a.localeCompare(b))
}

function prettifyAction(action: string): string {
    return action
        .toLowerCase()
        .split('_')
        .map(part => part ? part.charAt(0).toUpperCase() + part.slice(1) : part)
        .join(' ')
}

function normalizeState(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

function isActivatedState(state: string): boolean {
    return state.includes('activ') || state.includes('enabl') || state === 'on'
}

function isDeactivatedState(state: string): boolean {
    return state.includes('deactiv') || state.includes('disabl') || state.includes('inactiv') || state === 'off'
}

function toggleGlueTriggerSummary(item: AuditTrailEntry): string {
    if (item.new_value && typeof item.new_value === 'object' && !Array.isArray(item.new_value)) {
        if ('state' in item.new_value) {
            const state = normalizeState(item.new_value.state)
            if (isDeactivatedState(state)) return 'Deactivated'
            if (isActivatedState(state)) return 'Activated'
        }
        if ('activated' in item.new_value) return 'Activated'
        if ('deactivated' in item.new_value) return 'Deactivated'
    }
    if (item.old_value && typeof item.old_value === 'object' && !Array.isArray(item.old_value)) {
        if ('activate' in item.old_value) {
            return item.old_value.activate ? 'Activated' : 'Deactivated'
        }
        if ('state' in item.old_value) {
            const state = normalizeState(item.old_value.state)
            if (isDeactivatedState(state)) return 'Deactivated'
            if (isActivatedState(state)) return 'Activated'
        }
    }
    return 'Trigger state changed'
}

function toggleAuditControlSummary(item: AuditTrailEntry): string {
    if (item.action === 'TOGGLE_AUDIT_CONTROL_STATE') {
        if (item.new_value && typeof item.new_value === 'object' && !Array.isArray(item.new_value)) {
            if ('state' in item.new_value) {
                const state = normalizeState(item.new_value.state)
                if (isDeactivatedState(state)) return 'Deactivated'
                if (isActivatedState(state)) return 'Activated'
            }
        }
        return 'Activated'
    }

    if (item.action === 'TOGGLE_AUDIT_CONTROL_VISIBILITY') {
        if (item.new_value && typeof item.new_value === 'object' && !Array.isArray(item.new_value)) {
            if ('state' in item.new_value) {
                const state = normalizeState(item.new_value.state)
                if (state === 'hidden') return 'Hidden'
                if (state === 'visible') return 'Visible'
            }
        }
        return 'Visible'
    }

    return 'Audit control changed'
}

function statusClass(status: string): string {
    const normalized = status.toLowerCase()
    if (['failed', 'error'].includes(normalized)) return 'failed'
    if (['partial_success', 'partial'].includes(normalized)) return 'partial'
    return 'success'
}

function buildSummary(item: AuditTrailEntry): string {
    if (item.action === 'TOGGLE_GLUE_TRIGGER_STATE') {
        return toggleGlueTriggerSummary(item)
    }
    if (item.action === 'TOGGLE_AUDIT_CONTROL_STATE' || item.action === 'TOGGLE_AUDIT_CONTROL_VISIBILITY') {
        return toggleAuditControlSummary(item)
    }
    const action = prettifyAction(item.action)
    const resource = item.resource_type.replace(/_/g, ' ')
    return `${action} ${resource} ${item.resource_id}`.trim()
}

function jsonPretty(value: AuditTrailEntry['old_value'] | AuditTrailEntry['new_value']): string {
    if (value == null) return '—'
    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

function pruneAuditParams(params: URLSearchParams): URLSearchParams {
    const next = new URLSearchParams(params)
    AUDIT_PARAM_KEYS.forEach(key => next.delete(key))
    return next
}

function matchesAuditScope(item: AuditTrailEntry, auditScope: 'global' | 'tenant'): boolean {
    const isGlobalAction = GLOBAL_AUDIT_ACTIONS.has(item.action)
    return auditScope === 'global'
        ? isGlobalAction
        : !isGlobalAction
}

export const AuditTrailFeed: React.FC<AuditTrailFeedProps> = ({ companyName: lockedCompanyName, hideCompanyFilter = false, auditScope = 'global' }) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const [items, setItems] = useState<AuditTrailEntry[]>([])
    const [facetItems, setFacetItems] = useState<AuditTrailEntry[]>([])
    const [total, setTotal] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [selected, setSelected] = useState<AuditTrailEntry | null>(null)
    const [page, setPage] = useState(() => Number(searchParams.get('auditPage') || '0'))
    const sort = (searchParams.get('auditSort') as AuditSort) || 'date_desc'
    const [search, setSearch] = useState(searchParams.get('auditSearch') || '')
    const initialCompanyName = auditScope === 'tenant'
        ? (lockedCompanyName || '')
        : ''
    const [companyName, setCompanyName] = useState(initialCompanyName)
    const [resourceType, setResourceType] = useState(searchParams.get('auditResourceType') || '')
    const [action, setAction] = useState(searchParams.get('auditAction') || '')
    const [actorEmail, setActorEmail] = useState(searchParams.get('auditActorEmail') || '')
    const status = searchParams.get('auditStatus') || ''
    const effectiveCompanyName = lockedCompanyName || companyName
    const effectiveScope = auditScope

    useEffect(() => {
        if (lockedCompanyName) {
            setCompanyName(lockedCompanyName)
        }
    }, [lockedCompanyName])

    useEffect(() => {
        if (auditScope === 'global') {
            setCompanyName('')
        }
    }, [auditScope])

    const updateQueryParams = useCallback((nextState: Record<string, string>) => {
        setSearchParams(prev => {
            const next = pruneAuditParams(prev)
            Object.entries(nextState).forEach(([key, value]) => {
                if (value) next.set(key, value)
            })
            return next
        }, { replace: true })
    }, [setSearchParams])

    useEffect(() => {
        const nextState: Record<string, string> = {
            auditPage: String(page),
            auditSort: sort,
            auditScope: effectiveScope,
            auditSearch: search,
            auditResourceType: resourceType,
            auditAction: action,
            auditActorEmail: actorEmail,
            auditStatus: status,
        }
        if (auditScope === 'tenant' && effectiveCompanyName) {
            nextState.auditCompany = effectiveCompanyName
        }
        updateQueryParams(nextState)
    }, [action, actorEmail, auditScope, effectiveCompanyName, effectiveScope, page, resourceType, search, sort, status, updateQueryParams])

    useEffect(() => {
        let active = true
        setIsLoading(true)
        setHasError(false)

        const params: Record<string, string | number> = {
            limit: DEFAULT_LIMIT,
            offset: page * DEFAULT_LIMIT,
        }
        if (effectiveCompanyName) params.company_name = effectiveCompanyName
        params.audit_scope = effectiveScope
        if (resourceType) params.resource_type = resourceType
        if (action) params.action = action
        if (actorEmail) params.actor_email = actorEmail
        if (status) params.status = status
        if (search) params.search = search
        if (sort) params.sort = sort

        newHttpService.get('/api/backoffice/audit-log', params)
            .then((res: AuditTrailListResponse) => {
                if (!active) return
                setItems(res.items || [])
                setTotal(res.total || 0)
            })
            .catch(() => {
                if (!active) return
                setHasError(true)
                setItems([])
                setTotal(0)
            })
            .finally(() => {
                if (!active) return
                setIsLoading(false)
            })

        return () => {
            active = false
        }
    }, [action, actorEmail, effectiveCompanyName, effectiveScope, page, resourceType, search, sort, status])

    useEffect(() => {
        let active = true
        const pageSize = 500

        const loadFacetItems = async () => {
            try {
                const allItems: AuditTrailEntry[] = []
                let offset = 0
                let totalCount = 0

                while (active) {
                    const params: Record<string, string | number> = {
                        limit: pageSize,
                        offset,
                        audit_scope: effectiveScope,
                    }
                    if (effectiveCompanyName) params.company_name = effectiveCompanyName

                    const res = await newHttpService.get('/api/backoffice/audit-log', params) as AuditTrailListResponse
                    const batch = res.items || []
                    allItems.push(...batch)
                    totalCount = res.total || allItems.length
                    if (batch.length < pageSize || allItems.length >= totalCount) {
                        break
                    }
                    offset += pageSize
                }

                if (active) {
                    setFacetItems(allItems)
                }
            } catch {
                if (active) {
                    setFacetItems([])
                }
            }
        }

        loadFacetItems()

        return () => {
            active = false
        }
    }, [effectiveCompanyName, effectiveScope])

    useEffect(() => {
        setPage(0)
    }, [action, actorEmail, effectiveCompanyName, effectiveScope, resourceType, search, sort, status])

    const visibleItems = useMemo(
        () => items.filter(item => matchesAuditScope(item, effectiveScope)),
        [effectiveScope, items]
    )

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / DEFAULT_LIMIT)), [total])

    const companyOptions = useMemo(() => uniqueSorted(facetItems.map(item => item.company_name)), [facetItems])
    const resourceTypeOptions = useMemo(() => uniqueSorted(facetItems.map(item => item.resource_type)), [facetItems])
    const actionOptions = useMemo(() => uniqueSorted(facetItems.map(item => item.action)), [facetItems])
    const actorEmailOptions = useMemo(() => uniqueSorted(facetItems.map(item => item.actor_email)), [facetItems])

    const summary = useMemo(() => {
        if (!selected) return null
        return buildSummary(selected)
    }, [selected])

    return (
        <div className="atf-root">
            <div className="atf-toolbar">
                    <div className="atf-toolbar-row">
                        <span className="atf-count">{total.toLocaleString()} records</span>
                        <input
                            className="atf-search"
                            value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search company, actor, action, resource..."
                    />
                    {!hideCompanyFilter && lockedCompanyName == null && (
                        <select className="atf-select" value={companyName} onChange={e => setCompanyName(e.target.value)}>
                            <option value="">All companies</option>
                            {companyOptions.map(option => <option key={option} value={option}>{option}</option>)}
                        </select>
                    )}
                    <select className="atf-select" value={resourceType} onChange={e => setResourceType(e.target.value)}>
                        <option value="">All resource types</option>
                        {resourceTypeOptions.map(option => <option key={option} value={option}>{formatSelectLabel(option)}</option>)}
                    </select>
                    <select className="atf-select" value={action} onChange={e => setAction(e.target.value)}>
                        <option value="">All actions</option>
                        {actionOptions.map(option => <option key={option} value={option}>{formatSelectLabel(option)}</option>)}
                    </select>
                        <select className="atf-select" value={actorEmail} onChange={e => setActorEmail(e.target.value)}>
                            <option value="">All actors</option>
                            {actorEmailOptions.map(option => <option key={option} value={option}>{option}</option>)}
                        </select>
                    </div>
            </div>

            <div className="atf-table-shell">
                {isLoading ? (
                    <div className="atf-loading"><Spinner /></div>
                ) : hasError ? (
                    <div className="atf-empty atf-empty--error">Failed to load audit trails. Try refreshing.</div>
                ) : visibleItems.length === 0 ? (
                    <div className="atf-empty">No audit trails match the current filters.</div>
                ) : (
                    <>
                        <div className="atf-table-scroll">
                            <table className="atf-table">
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Actor</th>
                                        <th>Action</th>
                                        <th>Resource</th>
                                        <th>Resource ID</th>
                                        <th>Summary</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleItems.map(item => (
                                        <tr
                                            key={item.id}
                                            onClick={() => setSelected(item)}
                                            className={`atf-row atf-row--${statusClass(item.status)}`}
                                        >
                                            <td>{utilService.formatDateTime(item.created_at)}</td>
                                            <td>
                                                <div className="atf-cell-stack">
                                                    <span>{item.actor_email}</span>
                                                    {item.actor_name && <span className="atf-cell-subtle">{item.actor_name}</span>}
                                                </div>
                                            </td>
                                            <td>{item.action}</td>
                                            <td>{item.resource_type}</td>
                                            <td className="atf-mono">{item.resource_id}</td>
                                            <td title={buildSummary(item)}>{buildSummary(item)}</td>
                                            <td><span className={`atf-status atf-status--${statusClass(item.status)}`}>{item.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="atf-footer">
                            <button type="button" disabled={page === 0} onClick={() => setPage(prev => Math.max(0, prev - 1))}>Prev</button>
                            <span>{page + 1} / {totalPages}</span>
                            <button type="button" disabled={(page + 1) * DEFAULT_LIMIT >= total} onClick={() => setPage(prev => prev + 1)}>Next</button>
                        </div>
                    </>
                )}
            </div>

            <SlideDrawer isOpen={!!selected} onClose={() => setSelected(null)}>
                {selected && (
                    <div className="atf-drawer">
                        <header className="atf-drawer-header">
                            <p className="atf-drawer-eyebrow">Audit Trail</p>
                            <h2>{selected.action}</h2>
                            <span className={`atf-status atf-status--${statusClass(selected.status)}`}>{selected.status}</span>
                        </header>

                        <section className="atf-detail-grid">
                            <div><span>Company</span><strong>{selected.company_name}</strong></div>
                            <div><span>Actor email</span><strong>{selected.actor_email}</strong></div>
                            <div><span>Actor name</span><strong>{selected.actor_name || '—'}</strong></div>
                            <div><span>Resource type</span><strong>{selected.resource_type}</strong></div>
                            <div><span>Resource ID</span><strong>{selected.resource_id}</strong></div>
                            <div><span>Time</span><strong>{utilService.formatDateTime(selected.created_at)}</strong></div>
                            <div><span>Request ID</span><strong>{selected.request_id || '—'}</strong></div>
                            <div><span>IP address</span><strong>{selected.ip_address || '—'}</strong></div>
                            <div><span>Reason</span><strong>{selected.reason || '—'}</strong></div>
                        </section>

                        <section className="atf-json-block">
                            <h3>Summary</h3>
                            <p>{summary}</p>
                        </section>

                        <section className="atf-json-block">
                            <h3>Old value</h3>
                            <pre>{jsonPretty(selected.old_value)}</pre>
                        </section>

                        <section className="atf-json-block">
                            <h3>New value</h3>
                            <pre>{jsonPretty(selected.new_value)}</pre>
                        </section>
                    </div>
                )}
            </SlideDrawer>
        </div>
    )
}
